import { useState, type ComponentProps } from "react"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import cn from "@/v2-ui/utils/utils.cn"
import TranslationSidebarCategoriesSkeleton from "@/v2-backoffice-client/translation/sidebar/TranslationSidebarCategoriesSkeleton"
import TranslationSidebarCategorySection from "@/v2-backoffice-client/translation/sidebar/TranslationSidebarCategorySection"
import TranslationSidebarPinnedSection from "@/v2-backoffice-client/translation/sidebar/TranslationSidebarPinnedSection"
import {
  buildPinnedSections,
  readCollapsedSections,
  writeCollapsedSections,
  type CollapsedSections,
} from "@/v2-backoffice-client/translation/sidebar/translation.sidebar.utils"
import type { TranslationView } from "@/v2-backoffice-client/translation/translation.constants"
import type {
  TranslationBoard,
  TranslationCategory,
} from "@/v2-backoffice-client/translation/translation.types"

export type TranslationSidebarProps = Omit<
  ComponentProps<"nav">,
  "onSelect"
> & {
  board: TranslationBoard
  // True until the catalog query resolves. board.rows/incompleteRows/
  // categories are already [] at that point (buildTranslationBoard always
  // returns arrays, never undefined) — indistinguishable from "genuinely
  // empty" without this, which is why the sidebar used to render as if
  // frozen while the catalog was still loading.
  categoriesLoading: boolean
  // Counts from the (cheap, cached) drift queries; null while loading.
  missingInCodeCount: number | null
  orphanedCount: number | null
  fallbackMismatchCount: number | null
  // Changes since the last successful publish; null while loading or when
  // no publish baseline exists yet.
  pendingPublishCount: number | null
  view: TranslationView
  categoryKey: string | null
}

function TranslationSidebar(props: TranslationSidebarProps) {
  const {
    board,
    categoriesLoading,
    missingInCodeCount,
    orphanedCount,
    fallbackMismatchCount,
    pendingPublishCount,
    view,
    categoryKey,
    className,
    ...navProps
  } = props

  const [categoryQuery, setCategoryQuery] = useState("")
  const [collapsedSections, setCollapsedSections] = useState<CollapsedSections>(
    readCollapsedSections,
  )

  function toggleSection(section: TranslationCategory["section"]) {
    setCollapsedSections((prev) => {
      const next = { ...prev, [section]: !prev[section] }
      writeCollapsedSections(next)
      return next
    })
  }

  const normalizedQuery = categoryQuery.trim().toLowerCase()

  // Auto-expand the section holding a newly selected category (deep link,
  // search selection) so the active item is never hidden behind a collapsed
  // header. One-shot per selection: an explicit toggle afterwards still wins,
  // and the stored preference is left untouched (session-only expand).
  // Guarded setState-during-render (the React "derive state from props"
  // pattern); the board-length check defers the shot until the catalog has
  // loaded, so a cold ?category= load still expands.
  const [seenCategoryKey, setSeenCategoryKey] = useState<string | null>(null)
  if (categoryKey !== seenCategoryKey && board.categories.length > 0) {
    setSeenCategoryKey(categoryKey)
    const activeSection = categoryKey
      ? board.categoryByKey.get(categoryKey)?.section
      : undefined
    if (activeSection && collapsedSections[activeSection]) {
      setCollapsedSections((prev) => ({ ...prev, [activeSection]: false }))
    }
  }

  const pinnedSections = buildPinnedSections({
    categoriesLoading,
    totalRowCount: board.rows.length,
    incompleteRowCount: board.incompleteRows.length,
    missingInCodeCount,
    orphanedCount,
    fallbackMismatchCount,
    pendingPublishCount,
  })

  return (
    <nav
      className={cn("translation-sidebar", className)}
      aria-label="Features navigation"
      {...navProps}
    >
      {pinnedSections.map((section) => (
        <TranslationSidebarPinnedSection
          key={section.label}
          section={section}
          activeView={view}
          activeCategoryKey={categoryKey}
        />
      ))}

      <div className="translation-sidebar-divider" role="presentation" />

      <div className="translation-sidebar-search">
        <Icon
          src="search"
          size={13}
          className="translation-sidebar-search-icon"
        />
        <Input
          size="sm"
          type="search"
          value={categoryQuery}
          onChange={(e) => setCategoryQuery(e.target.value)}
          placeholder="Search categories..."
          aria-label="Search categories"
          className="translation-sidebar-search-input"
          data-feature-code="UI_BackofficeTranslation_Sidebar_CategorySearch"
        />
      </div>

      <div className="translation-sidebar-scroll">
        {categoriesLoading ? (
          <TranslationSidebarCategoriesSkeleton />
        ) : (
          (["ui", "data"] as const).map((section) => (
            <TranslationSidebarCategorySection
              key={section}
              section={section}
              categories={board.categories.filter(
                (category) => category.section === section,
              )}
              query={normalizedQuery}
              isCollapsed={!!collapsedSections[section]}
              activeCategoryKey={categoryKey}
              isCatalogActive={view === "catalog"}
              onToggle={() => toggleSection(section)}
            />
          ))
        )}
      </div>
    </nav>
  )
}

export default TranslationSidebar
